import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

const isNil = (v) => v === undefined || v === null || v === '';

export default function useListQueryParams(defaults = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  const defaultsRef = useRef(defaults);
  // Keep defaultsRef updated if default values actually change
  const serializedDefaults = useMemo(() => JSON.stringify(defaults), [defaults]);
  useEffect(() => {
    defaultsRef.current = defaults;
  }, [serializedDefaults]);

  // Compute parsed query object from current URL searchParams + defaults
  const parseParams = useCallback((params) => {
    const curDefaults = defaultsRef.current || {};
    const next = { ...curDefaults };
    Object.keys(curDefaults).forEach((key) => {
      const raw = params.get(key);
      if (raw !== null) next[key] = raw;
    });
    return next;
  }, []);

  // Synchronous state for immediate UI responsiveness (prevents cursor jumping in inputs)
  const [query, setQueryState] = useState(() => parseParams(searchParams));

  // Keep a ref to the latest local query for debounce comparison
  const latestQueryRef = useRef(query);
  latestQueryRef.current = query;

  const debounceTimerRef = useRef(null);

  // Sync state if URL searchParams changed externally (e.g. browser back/forward or external navigation)
  useEffect(() => {
    const urlQuery = parseParams(searchParams);
    const current = latestQueryRef.current;

    // Check if URL params actually differ from current local query
    let hasDifference = false;
    const allKeys = new Set([...Object.keys(current), ...Object.keys(urlQuery)]);
    for (const key of allKeys) {
      if (String(current[key] ?? '') !== String(urlQuery[key] ?? '')) {
        hasDifference = true;
        break;
      }
    }

    if (hasDifference) {
      setQueryState(urlQuery);
    }
  }, [searchParams, parseParams]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const commitToUrl = useCallback(
    (targetQuery) => {
      const next = new URLSearchParams();
      Object.entries(targetQuery).forEach(([key, value]) => {
        if (!isNil(value)) next.set(key, String(value));
      });
      // Only update if URLSearchParams actually differs
      if (next.toString() !== searchParamsRef.current.toString()) {
        setSearchParams(next, { replace: true });
      }
    },
    [setSearchParams]
  );

  const setQuery = useCallback(
    (patch, options = {}) => {
      const { resetPage = true, debounce = 350 } = options;
      const prev = latestQueryRef.current;
      const merged = { ...prev, ...patch };

      if (resetPage && Object.keys(patch).some((k) => k !== 'page')) {
        merged.page = '1';
      }

      // 1. Immediately update local state so controlled inputs update synchronously.
      // React synthetic events batch this in-render, preserving cursor position.
      latestQueryRef.current = merged;
      setQueryState(merged);

      // 2. Debounce URL sync if editing search, otherwise update URL immediately
      const isSearchEdit = 'search' in patch;

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      if (isSearchEdit && debounce > 0) {
        debounceTimerRef.current = setTimeout(() => {
          commitToUrl(latestQueryRef.current);
        }, debounce);
      } else {
        commitToUrl(merged);
      }
    },
    [commitToUrl]
  );

  return { query, setQuery };
}
